/**
 * Server-side sitemap: /sitemap.xml
 *
 * Referenced from public/robots.txt. It lists only the entity detail pages that
 * are actually indexable: studio and instructor slugs. The backend does the
 * filtering (hidden studios, soft-deleted instructors) so this route cannot
 * drift from the visibility rules by re-implementing them.
 *
 * The GET handler object is kept as a standalone const for the same reason as
 * app/routes/api/health.ts: @tanstack/react-start dropped `server` from
 * createFileRoute's parameter type while the runtime still honours it.
 */
import { createFileRoute } from '@tanstack/react-router';
import { API_CONFIG } from '@/lib/config/api.config';
import { getSiteOrigin } from '@/lib/utils/site-meta';

// Both values come from the app's own configuration rather than re-reading env
// vars here: API_CONFIG throws at import when VITE_API_BASE_URL is missing, so a
// misconfigured deployment fails loudly instead of serving an empty sitemap.
const SITE_ORIGIN = getSiteOrigin();
const API_BASE = API_CONFIG.BASE_URL;

interface SitemapPayload {
  studios: string[];
  instructors: string[];
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

export function buildSitemap(payload: SitemapPayload): string {
  const paths = [
    '/discovery',
    ...payload.studios.map((slug) => `/studios/${slug}`),
    ...payload.instructors.map((slug) => `/instructors/${slug}`),
  ];

  const urls = paths
    .map((path) => `  <url><loc>${escapeXml(`${SITE_ORIGIN}${path}`)}</loc></url>`)
    .join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${urls}\n</urlset>\n`;
}

const sitemapRouteOptions = {
  server: {
    handlers: {
      GET: async () => {
        try {
          const response = await fetch(`${API_BASE}/v2/sitemap`);
          if (!response.ok) {
            throw new Error(`sitemap fetch failed: ${response.status}`);
          }
          const body = (await response.json()) as { data?: SitemapPayload } | SitemapPayload;
          const payload = 'data' in body && body.data ? body.data : (body as SitemapPayload);
          return new Response(buildSitemap(payload), {
            headers: {
              'Content-Type': 'application/xml; charset=utf-8',
              'Cache-Control': 'public, max-age=300',
            },
          });
        } catch {
          // A crawler must never receive a 500 sitemap: an empty urlset is a
          // valid document and simply leaves the existing index untouched.
          return new Response(buildSitemap({ studios: [], instructors: [] }), {
            headers: {
              'Content-Type': 'application/xml; charset=utf-8',
              'Cache-Control': 'no-store',
            },
          });
        }
      },
    },
  },
};

export const Route = createFileRoute('/sitemap.xml')(sitemapRouteOptions);
